import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, RemoveShoppingCart as RemoveShoppingCartIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ordenCompraService, {
  CancelarCantidadItem,
  OrdenCompraPendienteLinea,
  OrdenCompraPendientes,
} from '../../services/ordenCompraService';
import { getApiErrorMessage } from '../../utils/apiError';

interface CancelarCantidadesOrdenCompraDialogProps {
  open: boolean;
  nroFormulario: number | null;
  titulo?: string;
  onClose: () => void;
  onSaved?: (result: OrdenCompraPendientes) => void;
}

const MOTIVO_MAX = 200;
const EPS = 0.0001;

const formatCant = (n: number | null | undefined): string =>
  Number(n ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const parseCant = (raw: string): number => {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '') return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const getErrorLinea = (linea: OrdenCompraPendienteLinea, raw: string): string | null => {
  const n = parseCant(raw);
  if (Number.isNaN(n)) return 'Valor inválido';
  if (n < 0) return 'No puede ser negativa';
  if (n - (linea.cantidadCanceladaMaxima ?? 0) > EPS) return `Máx. ${formatCant(linea.cantidadCanceladaMaxima)}`;
  return null;
};

const CancelarCantidadesOrdenCompraDialog: React.FC<CancelarCantidadesOrdenCompraDialogProps> = ({
  open,
  nroFormulario,
  titulo,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [data, setData] = useState<OrdenCompraPendientes | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [valores, setValores] = useState<Record<number, string>>({});
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open || nroFormulario == null) return;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setMotivo('');
    setValores({});
    setLoading(true);
    ordenCompraService
      .getPendientesOrdenCompra(nroFormulario)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        const init: Record<number, string> = {};
        (res.lineas ?? []).forEach((l) => {
          init[l.nroItem] = String(l.cantidadCancelada ?? 0);
        });
        setValores(init);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadError(getApiErrorMessage(error, 'Error al obtener las cantidades pendientes de la orden de compra'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, nroFormulario]);

  const lineas = useMemo(() => data?.lineas ?? [], [data]);

  const errores = useMemo(() => {
    const map: Record<number, string | null> = {};
    lineas.forEach((l) => {
      map[l.nroItem] = getErrorLinea(l, valores[l.nroItem] ?? '');
    });
    return map;
  }, [lineas, valores]);

  const itemsModificados = useMemo<CancelarCantidadItem[]>(
    () =>
      lineas
        .filter((l) => !errores[l.nroItem])
        .map((l) => ({ nroItem: l.nroItem, cantidadCancelada: parseCant(valores[l.nroItem] ?? '') }))
        .filter((it) => {
          const original = lineas.find((l) => l.nroItem === it.nroItem)?.cantidadCancelada ?? 0;
          return Math.abs(it.cantidadCancelada - original) > EPS;
        }),
    [lineas, valores, errores]
  );

  const hayErrores = Object.values(errores).some(Boolean);
  const puedeCancelar = Boolean(data?.puedeCancelarCantidades);
  const hayPendiente = (data?.totalPendiente ?? 0) > EPS;
  const busy = guardando || loading;

  const motivoPayload = motivo.trim() || null;

  const finalizar = (result: OrdenCompraPendientes, mensaje: string) => {
    enqueueSnackbar(mensaje, { variant: 'success' });
    onSaved?.(result);
    onClose();
  };

  const handleAceptar = async () => {
    if (nroFormulario == null || busy || !puedeCancelar || hayErrores) return;
    if (itemsModificados.length === 0) {
      enqueueSnackbar('No hay cambios en las cantidades canceladas', { variant: 'info' });
      return;
    }
    try {
      setGuardando(true);
      const result = await ordenCompraService.cancelarCantidadesOrdenCompra(nroFormulario, {
        cancelarTodo: false,
        items: itemsModificados,
        motivo: motivoPayload,
      });
      finalizar(result, 'Cantidades canceladas correctamente');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cancelar las cantidades'), { variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const handleCancelarTodo = async () => {
    if (nroFormulario == null || busy || !puedeCancelar || !hayPendiente) return;
    if (
      !window.confirm(
        '¿Está seguro que desea cancelar todas las cantidades pendientes de recibir de esta orden de compra?\n\nLa orden de compra no se anula.'
      )
    )
      return;
    try {
      setGuardando(true);
      const result = await ordenCompraService.cancelarCantidadesOrdenCompra(nroFormulario, {
        cancelarTodo: true,
        items: null,
        motivo: motivoPayload,
      });
      finalizar(result, 'Se cancelaron todas las cantidades pendientes');
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cancelar el total pendiente'), { variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1 }}>
          Cantidades de la Orden de Compra
          {titulo ? (
            <Typography variant="body2" color="text.secondary">
              {titulo}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} disabled={guardando} aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : data ? (
          <>
            {!puedeCancelar && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {data.anulada
                  ? 'La orden de compra está anulada.'
                  : !data.aprobada
                    ? 'La orden de compra debe estar aprobada para poder cancelar cantidades.'
                    : 'Esta orden de compra no admite cancelación de cantidades.'}
              </Alert>
            )}
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Pedida
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Recibida
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }} align="right">
                      Cancelada
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Pendiente de recibir
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        La orden de compra no tiene ítems.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineas.map((l) => {
                      const raw = valores[l.nroItem] ?? '';
                      const error = errores[l.nroItem];
                      const cancelada = error ? l.cantidadCancelada : parseCant(raw);
                      const pendiente = Math.max(0, l.cantidadPedida - l.cantidadRecibida - cancelada);
                      return (
                        <TableRow key={l.nroItem}>
                          <TableCell>{l.codigoArticulo || '-'}</TableCell>
                          <TableCell>{l.descripcionArticulo || '-'}</TableCell>
                          <TableCell>{l.um || ''}</TableCell>
                          <TableCell align="right">{formatCant(l.cantidadPedida)}</TableCell>
                          <TableCell align="right">{formatCant(l.cantidadRecibida)}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={raw}
                              onChange={(e) => setValores((prev) => ({ ...prev, [l.nroItem]: e.target.value }))}
                              onFocus={(e) => e.target.select()}
                              disabled={!puedeCancelar || guardando || (l.cantidadCanceladaMaxima ?? 0) <= EPS}
                              error={Boolean(error)}
                              helperText={error || undefined}
                              inputProps={{
                                min: 0,
                                max: l.cantidadCanceladaMaxima,
                                step: 'any',
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: 140 }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatCant(pendiente)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Total pendiente de recibir: <strong>{formatCant(data.totalPendiente)}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Motivo (opcional)"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, MOTIVO_MAX))}
              disabled={!puedeCancelar || guardando}
              helperText={`${motivo.length}/${MOTIVO_MAX}`}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<RemoveShoppingCartIcon />}
          onClick={() => void handleCancelarTodo()}
          disabled={busy || !puedeCancelar || !hayPendiente}
          sx={{ textTransform: 'none', mr: 'auto' }}
        >
          Cancelar todo lo pendiente
        </Button>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleAceptar()}
          disabled={busy || !puedeCancelar || hayErrores || itemsModificados.length === 0}
          startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          Aceptar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelarCantidadesOrdenCompraDialog;
